import { useEffect, useRef } from 'react';

// ── Shaders ──────────────────────────────────────────────────────────────────

const VERT = `
  attribute vec2 aPos;
  void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

// Long static gradient (top sky-blue → mid lavender → bottom darker plum),
// + animated curling wisps confined to the hero region (top viewport),
// + truly async twinkling stars that pick new cells each cycle.
const FRAG = `
  precision highp float;
  uniform vec2  uRes;
  uniform float uTime;
  uniform float uMotion;
  uniform float uHeroFrac;     // heroSectionHeight / canvasHeight
  uniform float uWispCenterY;  // hero-relative Y where the wisps cluster (1=top, 0=bottom of hero)
  uniform float uWispScale;    // size multiplier for wisp len/thick (mobile = smaller)

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p); vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = hash(i), b = hash(i+vec2(1,0)), c = hash(i+vec2(0,1)), d = hash(i+vec2(1,1));
    return mix(mix(a,b,f.x), mix(c,d,f.x), f.y);
  }
  float fbm(vec2 p) {
    float v=0.0, a=0.5;
    for(int i=0;i<5;i++){v+=a*vnoise(p);p=p*2.05+vec2(2.7,1.3);a*=0.5;}
    return v;
  }

  // Cosmic wisp: elongated, organically curved gaussian streamer.
  // curl   - parabolic tilt (monotonic bend in one direction)
  // wavePh - phase for an S-curve sinusoid along the streamer length,
  //          giving each wisp a wavy, snake-like shape that breathes over time.
  float wisp(vec2 uv, float ar, vec2 center, float angle,
             float len, float thick, float curl, float wavePh) {
    vec2 rel = (uv - center) * vec2(ar, 1.0);
    float c = cos(angle), s = sin(angle);
    float along = c * rel.x + s * rel.y;
    float t     = along / max(len, 0.0001);   // normalised, -1..1 inside the wisp
    float perp  = (-s * rel.x + c * rel.y);
    // Parabolic tilt (the original curl behaviour, quadratic for true bend)
    perp -= along * along * sign(curl) * abs(curl) * 0.55;
    // S-curve waviness — a sin along the length with per-wisp phase
    perp -= sin(t * 2.7 + wavePh) * thick * 2.0;
    // Secondary higher-frequency ripple, weaker, for organic detail
    perp -= sin(t * 5.3 - wavePh * 0.6) * thick * 0.55;
    return exp(-(perp/thick)*(perp/thick))
         * max(0.0, 1.0 - t*t);
  }
  float wispEnv(float ph) {
    return smoothstep(0.0, 0.18, ph) * smoothstep(1.0, 0.72, ph);
  }

  // Palette — deeper tones for stronger contrast against glass-morphism cards
  const vec3 C_TOP   = vec3(0.36, 0.50, 0.78); // softer dusk-blue at very top
  const vec3 C_MID   = vec3(0.42, 0.34, 0.66); // muted purple mid
  const vec3 C_DEEP  = vec3(0.16, 0.12, 0.32); // very deep plum bottom
  const vec3 C_WARM  = vec3(1.00, 0.76, 0.84); // warm pink halo
  const vec3 C_PEACH = vec3(1.00, 0.86, 0.82);
  const vec3 C_HOT   = vec3(1.00, 0.58, 0.78); // saturated rose-pink core
  const vec3 C_CREAM = vec3(1.00, 0.94, 0.90);
  const vec3 C_BLUE  = vec3(0.68, 0.86, 1.00);
  const vec3 C_ICE   = vec3(0.88, 0.96, 1.00);

  void main() {
    vec2 uv = gl_FragCoord.xy / uRes;       // y=0 bottom, y=1 top
    float ar = uRes.x / uRes.y;             // canvas aspect (very tall)

    // Distance from the top of the canvas (0 at top, 1 at bottom).
    float t = 1.0 - uv.y;

    // Three-stop vertical gradient.
    vec3 col = mix(C_TOP, C_MID, smoothstep(0.0, 0.42, t));
    col      = mix(col,  C_DEEP, smoothstep(0.55, 1.0, t));

    // Subtle horizontal warmth on the right edge.
    col = mix(col, vec3(0.50, 0.36, 0.66), smoothstep(0.65, 1.0, uv.x) * 0.10);

    // ── Wisps: hero-region only ─────────────────────────────────────────────
    // Hero occupies the top uHeroFrac of canvas (uv.y in [1 - uHeroFrac, 1]).
    // Map UV into hero-relative coords so wisp positions/sizes are in
    // viewport units, then mask to fade out below the hero.
    float heroY     = (uv.y - (1.0 - uHeroFrac)) / max(uHeroFrac, 0.0001);
    vec2  heroUV    = vec2(uv.x, heroY);
    float heroAr    = ar / max(uHeroFrac, 0.0001);
    // Only render wisps where heroY is roughly in [0, 1.05]
    float heroMask  = smoothstep(-0.05, 0.10, heroY) * smoothstep(1.10, 0.85, heroY);

    // FBM modulation for organic flicker
    float n = fbm(heroUV * vec2(0.6, 1.4) * 0.9 + vec2(uTime * 0.07, -uTime * 0.04));

    // Bloom behind cards — radius scales with uWispScale, center follows uWispCenterY.
    float bloomR = 0.40 / max(uWispScale, 0.0001);
    float bloomRy = 0.72 / max(uWispScale, 0.0001);
    float bx = (heroUV.x - 0.68) * bloomR;
    float by = (heroUV.y - uWispCenterY) * bloomRy;
    float bloom = exp(-(bx*bx + by*by)) * (0.5 + 0.5 * smoothstep(0.3, 0.8, n));
    // Cards mask follows wisp center too
    float cmRange = 0.35 * uWispScale;
    float cardsMask = smoothstep(0.30, 0.55, heroUV.x)
                    * smoothstep(uWispCenterY - cmRange, uWispCenterY - cmRange*0.3, heroY)
                    * smoothstep(uWispCenterY + cmRange*1.4, uWispCenterY + cmRange*0.4, heroY)
                    * heroMask;
    col = mix(col, C_WARM,  bloom * 0.85 * cardsMask);
    col = mix(col, C_PEACH, bloom * bloom * 0.75 * cardsMask);
    // Saturated rose-pink core where the bloom is brightest
    col = mix(col, C_HOT,   pow(bloom, 2.4) * 0.62 * cardsMask);

    // 5 curling wisps shifted into hero-relative space — sized 2.5× and given
    // organic per-wisp wave phases that drift with time, so each strand bends
    // and breathes independently rather than reading as a straight ray.
    // Wisp positions — y anchored to uWispCenterY, drift scaled by uWispScale.
    // Original anchors (desktop): 0.56, 0.60, 0.52, 0.52, 0.58 — centred near 0.56.
    // Below they're expressed as offsets from that centre.
    float ws = uWispScale;
    float wcy = uWispCenterY;

    float ph1 = fract(uTime * 0.100 + 0.00);
    vec2  c1  = vec2(0.62, wcy + 0.00) + vec2( 0.07, -0.04) * ph1 * ws;
    float wv1 = uTime * 0.42 + 0.30;
    float w1h = wisp(heroUV, heroAr, c1,  0.72, 0.430 * ws, 0.0675 * ws, 2.60, wv1) * wispEnv(ph1);
    float w1c = wisp(heroUV, heroAr, c1,  0.72, 0.430 * ws, 0.0200 * ws, 2.55, wv1) * wispEnv(ph1);

    float ph2 = fract(uTime * 0.125 + 0.45);
    vec2  c2  = vec2(0.85, wcy + 0.04) + vec2(-0.06, -0.05) * ph2 * ws;
    float wv2 = uTime * 0.36 + 1.85;
    float w2h = wisp(heroUV, heroAr, c2, -0.42, 0.413 * ws, 0.0600 * ws,-2.30, wv2) * wispEnv(ph2);
    float w2c = wisp(heroUV, heroAr, c2, -0.42, 0.413 * ws, 0.0175 * ws,-2.25, wv2) * wispEnv(ph2);

    float ph3 = fract(uTime * 0.083 + 0.70);
    vec2  c3  = vec2(0.73, wcy - 0.04) + vec2( 0.05, -0.06) * ph3 * ws;
    float wv3 = uTime * 0.48 + 3.40;
    float w3h = wisp(heroUV, heroAr, c3,  1.15, 0.375 * ws, 0.0550 * ws, 2.90, wv3) * wispEnv(ph3);

    float ph4 = fract(uTime * 0.111 + 0.75);
    vec2  c4  = vec2(0.58, wcy - 0.04) + vec2( 0.08, -0.06) * ph4 * ws;
    float wv4 = uTime * 0.39 + 5.20;
    float w4h = wisp(heroUV, heroAr, c4,  1.85, 0.420 * ws, 0.0600 * ws,-2.60, wv4) * wispEnv(ph4);
    float w4c = wisp(heroUV, heroAr, c4,  1.85, 0.420 * ws, 0.0175 * ws,-2.55, wv4) * wispEnv(ph4);

    float ph5 = fract(uTime * 0.091 + 0.28);
    vec2  c5  = vec2(0.88, wcy + 0.02) + vec2(-0.07, -0.04) * ph5 * ws;
    float wv5 = uTime * 0.45 + 2.10;
    float w5h = wisp(heroUV, heroAr, c5, -0.52, 0.405 * ws, 0.0575 * ws, 2.70, wv5) * wispEnv(ph5);
    float w5c = wisp(heroUV, heroAr, c5, -0.52, 0.405 * ws, 0.0150 * ws, 2.65, wv5) * wispEnv(ph5);

    col = mix(col, C_WARM,  clamp(w1h*0.76 + w2h*0.70 + w3h*0.52, 0.0, 1.0) * heroMask);
    col = mix(col, C_PEACH, clamp(w1h*0.64 + w2h*0.57, 0.0, 1.0) * heroMask * 0.82);
    col = mix(col, C_CREAM, clamp(w1c*0.96 + w2c*0.90, 0.0, 1.0) * heroMask);
    col = mix(col, C_BLUE,  clamp(w4h*0.66 + w5h*0.60, 0.0, 1.0) * heroMask);
    col = mix(col, C_ICE,   clamp(w4c*0.93 + w5c*0.86, 0.0, 1.0) * heroMask);

    // ── Async twinkling stars: per-epoch random gating + position ───────────
    // Square cells in screen pixels.
    float cellN = 80.0;
    vec2 sp  = uv * vec2(cellN, cellN / ar);
    vec2 sId = floor(sp);
    vec2 sF  = fract(sp);

    // Per-cell static seeds for clock period and phase offset
    float seedPeriod = hash(sId + vec2(17.3, 41.7));
    float seedOffset = hash(sId + vec2(53.1, 27.9));

    float period    = 4.0 + seedPeriod * 6.0;
    float starClock = uTime * uMotion / period + seedOffset * 200.0;
    float starPhase = fract(starClock);
    float epoch     = floor(starClock);

    // Per-epoch gate: each cycle picks a *new random* set of cells to twinkle.
    float gate = step(0.93, hash(sId + vec2(epoch * 13.71, epoch * 31.37)));

    // New random position inside the cell each epoch.
    vec2 starPos = vec2(
      0.1 + 0.8 * hash(sId + vec2(epoch * 0.37 + 3.1, epoch * 0.61 + 7.7)),
      0.1 + 0.8 * hash(sId + vec2(epoch * 0.71 + 9.3, epoch * 0.43 + 2.1))
    );

    float bright = smoothstep(0.0, 0.20, starPhase) * smoothstep(1.0, 0.78, starPhase);
    bright *= 0.45 + 0.55 * hash(sId + vec2(epoch * 0.13 + 1.1, epoch * 0.87 + 5.3));

    float starD = length(sF - starPos);
    col += vec3(exp(-starD * 30.0) * bright * 0.85 * gate);

    col = clamp(col, 0.0, 1.0);
    gl_FragColor = vec4(col, 1.0);
  }
`;

// ── WebGL helpers ────────────────────────────────────────────────────────────

function compile(gl: WebGLRenderingContext, type: number, src: string) {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  return s;
}

function buildProgram(gl: WebGLRenderingContext) {
  const p = gl.createProgram()!;
  gl.attachShader(p, compile(gl, gl.VERTEX_SHADER,   VERT));
  gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, FRAG));
  gl.linkProgram(p);
  return p;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function GradientBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (window.innerWidth < 768 || /Mobi|Android/i.test(navigator.userAgent)) return;

    let cleanupFn: (() => void) | undefined;

    const initGL = () => {
      const gl = canvas.getContext('webgl', { antialias: false, alpha: false });
      if (!gl) return;

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const prog = buildProgram(gl);
      gl.useProgram(prog);

      const uRes         = gl.getUniformLocation(prog, 'uRes');
      const uTime        = gl.getUniformLocation(prog, 'uTime');
      const uMotion      = gl.getUniformLocation(prog, 'uMotion');
      const uHeroFrac    = gl.getUniformLocation(prog, 'uHeroFrac');
      const uWispCenterY = gl.getUniformLocation(prog, 'uWispCenterY');
      const uWispScale   = gl.getUniformLocation(prog, 'uWispScale');

      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      const aPos = gl.getAttribLocation(prog, 'aPos');
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

      let heroFrac = 0.5, wispCenterY = 0.56, wispScale = 1.0;
      let pendingHeightResize = 0, lastWidthDpr = 0;

      const measureHero = () => {
        const hero  = document.getElementById('hero');
        const cards = document.querySelector('.hero-cards-grid') as HTMLElement | null;
        const cssH  = canvas.clientHeight || window.innerHeight;
        if (hero) {
          const heroH = hero.clientHeight || window.innerHeight;
          heroFrac = Math.max(0.05, Math.min(1.0, heroH / cssH));
          if (cards) {
            const heroTop  = hero.getBoundingClientRect().top + window.scrollY;
            const cardsTop = cards.getBoundingClientRect().top + window.scrollY;
            const fromTopFrac = (cardsTop - heroTop + cards.clientHeight / 2) / heroH;
            wispCenterY = Math.max(0.10, Math.min(0.92, 1 - fromTopFrac));
          } else { wispCenterY = 0.56; }
        } else {
          heroFrac = Math.max(0.05, Math.min(1.0, window.innerHeight / cssH));
          wispCenterY = 0.56;
        }
        wispScale = window.innerWidth < 860 ? 0.55 : 1.0;
      };

      const resizeBuffer = () => {
        const dpr = Math.min(window.devicePixelRatio || 1, 1.0);
        const w = canvas.clientWidth, h = canvas.clientHeight;
        if (w === 0 || h === 0) return;
        const tw = Math.floor(w * dpr), th = Math.floor(h * dpr);
        if (canvas.width !== tw || canvas.height !== th) {
          canvas.width = tw; canvas.height = th;
          gl.viewport(0, 0, tw, th);
        }
        measureHero(); lastWidthDpr = tw;
      };
      resizeBuffer();

      const onResize = () => {
        const dpr = Math.min(window.devicePixelRatio || 1, 1.0);
        const tw  = Math.floor(canvas.clientWidth * dpr);
        if (tw !== lastWidthDpr) { window.clearTimeout(pendingHeightResize); resizeBuffer(); return; }
        window.clearTimeout(pendingHeightResize);
        pendingHeightResize = window.setTimeout(resizeBuffer, 120) as unknown as number;
      };
      window.addEventListener('resize', onResize, { passive: true });

      const ro = new ResizeObserver(onResize);
      ro.observe(canvas);
      if (canvas.parentElement) ro.observe(canvas.parentElement);

      const heroEl  = document.getElementById('hero');
      const cardsEl = document.querySelector('.hero-cards-grid') as HTMLElement | null;
      const heroRo  = new ResizeObserver(() => measureHero());
      if (heroEl)  heroRo.observe(heroEl);
      if (cardsEl) heroRo.observe(cardsEl);
      requestAnimationFrame(() => measureHero());

      const start = performance.now();
      let raf = 0;
      const draw = () => {
        raf = requestAnimationFrame(draw);
        const t = (performance.now() - start) / 1000;
        gl.uniform2f(uRes, canvas.width, canvas.height);
        gl.uniform1f(uTime, t);
        gl.uniform1f(uMotion, reduce ? 0.0 : 1.0);
        gl.uniform1f(uHeroFrac, heroFrac);
        gl.uniform1f(uWispCenterY, wispCenterY);
        gl.uniform1f(uWispScale, wispScale);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      };
      draw();

      cleanupFn = () => {
        cancelAnimationFrame(raf);
        window.clearTimeout(pendingHeightResize);
        window.removeEventListener('resize', onResize);
        ro.disconnect(); heroRo.disconnect();
        gl.deleteProgram(prog); gl.deleteBuffer(buf);
      };
    };

    let scheduleId: number | ReturnType<typeof setTimeout>;
    if (typeof requestIdleCallback !== 'undefined') {
      scheduleId = requestIdleCallback(initGL, { timeout: 2000 });
    } else {
      scheduleId = setTimeout(initGL, 200);
    }

    return () => {
      typeof cancelIdleCallback !== 'undefined'
        ? cancelIdleCallback(scheduleId as number)
        : clearTimeout(scheduleId as ReturnType<typeof setTimeout>);
      cleanupFn?.();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
        background: 'linear-gradient(180deg, #5C80C7 0%, #6B57A8 45%, #281F52 100%)',
      }}
    />
  );
}
